import { isPageContextEnabled, resolveSourcePageType } from "@/components/analytics/pageContext";
import { trackedSourceUrls } from "@/components/analytics/sanitizeTrackedUrl";
import { ConsentObject, ConsentPurpose, ConsentStatus } from "./ConsentTypes";

// Event shape emitted by Salesforce's own "sfmc-sitemap" beacon template, so
// our events land in the same Data Cloud website-engagement objects.
export interface SalesforceInteractionEvent {
    interaction: {
        name: string;
        eventType?: string;
        screenHeight?: number;
        screenWidth?: number;
        sourcePageTitle?: string;
        sourceDomainName?: string;
        /** Click events only, behind NEXT_PUBLIC_SF_PAGE_CONTEXT: the clicked link's path. */
        targetPath?: string;
    };
    source?: {
        locale: string;
        pageType: string;
        /** Behind NEXT_PUBLIC_SF_PAGE_CONTEXT: the page URL with personal data stripped. The SDK fills in location.href when absent. */
        url?: string;
        /** Behind NEXT_PUBLIC_SF_PAGE_CONTEXT: the referrer, cleaned the same way. The SDK fills in document.referrer when absent. */
        urlReferrer?: string;
    };
    user?: {
        identities?: Record<string, string>;
        attributes?: Record<string, string>;
    };
}

// Known-visitor profile event in the Data 360 web connector shape: no
// interaction, just `user.attributes` whose `eventType` routes it to the
// connector schema's `identity` profile object. (The blank `isAnonymous: 1`
// profile is not ours to send: the SDK attaches it itself, see below.)
export interface SalesforceIdentityProfileEvent {
    user: {
        attributes: {
            eventType: "identity";
            isAnonymous: 0;
            firstName: string;
            lastName: string;
        };
    };
}

// The visitor's email, as its own profile event: the connector schema keeps the
// address on `contactPointEmail` (the `identity` object has no email field), and
// it is what lets identity resolution match the browser to a CRM Contact.
export interface SalesforceContactPointEmailEvent {
    user: {
        attributes: {
            eventType: "contactPointEmail";
            email: string;
        };
    };
}

export type SalesforceProfileEvent = SalesforceIdentityProfileEvent | SalesforceContactPointEmailEvent;

// One entry of the SDK's internal consent store, as returned by getConsents().
interface SalesforceConsentRecord {
    consent: ConsentObject;
    lastUpdateTime?: string;
    lastSentTime?: string;
}

declare global {
    interface Window {
        SalesforceInteractions?: {
            init: (config: { consents: ConsentObject[]; cookieDomain: string }) => Promise<void>;
            updateConsents: (consents: ConsentObject[]) => void;
            getConsents: () => SalesforceConsentRecord[];
            // Profile overload first so the interaction overload stays the one
            // inferred when a test reads a mock's last call.
            sendEvent: {
                (event: SalesforceProfileEvent): void;
                (event: SalesforceInteractionEvent): void;
            };
            ConsentStatus: typeof ConsentStatus;
            ConsentPurpose: typeof ConsentPurpose;
        };
        sfmc?: {
            consentBanner?: {
                consentManager?: { getCookieDomain: () => string | null };
            };
        };
    }
}

/**
 * Profile (identity) events are gated by NEXT_PUBLIC_SF_PROFILE_EVENTS. Data 360
 * drops them until the connector schema has the `identity` object, so the flag
 * defaults off and, when off, nothing here changes how the site behaves today.
 * Next inlines NEXT_PUBLIC_* values at build time, so changing it needs a
 * rebuild; it is read at call time only so tests can stub it.
 */
export function isSalesforceProfileEventsEnabled(): boolean {
    const raw = process.env.NEXT_PUBLIC_SF_PROFILE_EVENTS?.trim().toLowerCase();
    return raw === "1" || raw === "true";
}

export interface KnownIdentity {
    email: string;
    firstName?: string;
    lastName?: string;
}

// The SDK instance whose init() has resolved. Comparing it with the live
// window.SalesforceInteractions (rather than keeping a plain boolean) means a
// replaced or reloaded SDK object is never mistaken for an initialised one.
let initialisedSdk: Window["SalesforceInteractions"];
// At most one identity waiting for the SDK to finish initialising; a newer
// submission replaces it, and it only ever lives in memory.
let pendingIdentity: KnownIdentity | null = null;

export function isSalesforceInteractionsReady(): boolean {
    if (typeof window === "undefined") return false;
    const sdk = window.SalesforceInteractions;
    return sdk !== undefined && sdk === initialisedSdk;
}

// The visitor's tracking consent as the SDK itself holds it. ConsentProvider
// pushes the banner state into the SDK (initially via init(), then
// updateConsents()), so this is the consent the visitor currently has and,
// unlike useConsent(), is readable outside React. Tracking is opted in when
// either Marketing or Analytics is accepted (see buildSalesforceConsents).
function isTrackingConsentGranted(): boolean {
    const tracking = (window.SalesforceInteractions?.getConsents?.() ?? []).filter(
        ({ consent }) => consent?.purpose === ConsentPurpose.Tracking,
    );
    return (
        tracking.length > 0 && tracking.every(({ consent }) => consent.status === ConsentStatus.OptIn)
    );
}

export function buildKnownProfileEvent({ firstName, lastName }: KnownIdentity): SalesforceIdentityProfileEvent {
    return {
        user: {
            attributes: {
                eventType: "identity",
                isAnonymous: 0,
                firstName: firstName?.trim() ?? "",
                lastName: lastName?.trim() ?? "",
            },
        },
    };
}

export function buildContactPointEmailEvent({ email }: KnownIdentity): SalesforceContactPointEmailEvent {
    return {
        user: {
            attributes: {
                eventType: "contactPointEmail",
                email: email.trim(),
            },
        },
    };
}

function sendProfileEvent(event: SalesforceProfileEvent): void {
    if (!isSalesforceInteractionsReady() || !isTrackingConsentGranted()) return;
    window.SalesforceInteractions?.sendEvent(event);
}

// A known visitor is two profile events: who (identity) and the address that
// links them to a Contact (contactPointEmail). Each re-checks consent.
function sendKnownProfile(identity: KnownIdentity): void {
    sendProfileEvent(buildKnownProfileEvent(identity));
    sendProfileEvent(buildContactPointEmailEvent(identity));
}

function flushPendingIdentity(): void {
    if (!isSalesforceInteractionsReady()) return;
    const identity = pendingIdentity;
    pendingIdentity = null;
    if (identity) sendKnownProfile(identity);
}

/**
 * Sends the visitor's known identity to Data 360 as a profile event, when
 * NEXT_PUBLIC_SF_PROFILE_EVENTS is on.
 *
 * - No SDK on the page: dropped. The beacon only loads after the visitor opts
 *   in, so an absent SDK means there is no consent to send under, and the
 *   identity is not kept around for a later opt-in.
 * - SDK present but still initialising: held in memory (one at most, the latest
 *   wins) and sent when init resolves.
 * - SDK ready: sent straight away.
 *
 * Whenever it is sent, the SDK must hold a tracking opt-in at that moment.
 */
export function sendKnownIdentity(identity: KnownIdentity): void {
    if (typeof window === "undefined" || !isSalesforceProfileEventsEnabled()) return;
    if (!identity.email.trim() || !window.SalesforceInteractions) return;
    if (!isSalesforceInteractionsReady()) {
        pendingIdentity = identity;
        return;
    }
    sendKnownProfile(identity);
}

// The beacon's bundled sitemap only initializes the SDK from a DOMContentLoaded
// listener, which has already fired by the time a client-injected next/script
// runs, so the SDK must be initialized from our onLoad handler instead.
export function initSalesforceInteractions(consents: ConsentObject[]): Promise<void> | undefined {
    const sdk = window.SalesforceInteractions;
    if (!sdk) return undefined;
    const cookieDomain =
        window.sfmc?.consentBanner?.consentManager?.getCookieDomain() ?? window.location.hostname;
    // The blank anonymous profile ({ eventType: "identity", isAnonymous: 1 }) is
    // deliberately not sent from here. The documented OnSetAnonymousId listener
    // sample predates the SDK's automatic anonymous identity event: this beacon
    // already attaches that profile to the first batch it sends after creating
    // a new anonymous id. A listener of our own doubled it on a first visit, and
    // since init() re-announces the stored id on every page load, it would also
    // have sent a blank profile on each return visit, over a known one.
    return sdk.init({ consents, cookieDomain }).then(() => {
        initialisedSdk = sdk;
        try {
            flushPendingIdentity();
        } catch (error) {
            // Profile events must never stop the page-view that follows init.
            console.warn("Salesforce profile event failed:", error);
        }
    });
}

export function sendWebsiteEngagementEvent(name: string, targetPath?: string | null): void {
    window.SalesforceInteractions?.sendEvent({
        interaction: {
            name,
            eventType: "website",
            screenHeight: window.screen.height,
            screenWidth: window.screen.width,
            sourcePageTitle: document.title,
            sourceDomainName: window.location.hostname,
            ...(isPageContextEnabled() && targetPath ? { targetPath } : {}),
        },
        source: {
            locale: navigator.language,
            pageType: resolveSourcePageType(),
            ...trackedSourceUrls(),
        },
    });
}

export function clickEngagementEventName(target: EventTarget | null): string | null {
    if (!(target instanceof Element)) return null;
    switch (target.tagName.toLowerCase()) {
        case "a":
            return "anchor-click";
        case "button":
            return "button-click";
        case "input":
            switch ((target as HTMLInputElement).type.toLowerCase()) {
                case "button":
                    return "input-type-button-click";
                case "submit":
                    return "input-type-submit-click";
                case "reset":
                    return "input-type-reset-click";
                default:
                    return null;
            }
        default:
            return null;
    }
}
